import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supervisorAPI } from '../../utils/api';
import { toast } from 'sonner';
import { MapPin, CheckCircle2, Clock, LogOut, RefreshCw, Key, X, Edit2, AlertTriangle, Calendar, ChevronLeft, ChevronRight, Search, UserX, Lock } from 'lucide-react';
import api from '../../utils/api';

const SupervisorDashboard = () => {
  const [parks, setParks] = useState([]);
  const [otParks, setOtParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showChangePinModal, setShowChangePinModal] = useState(false);
  const [showWorkerCalendar, setShowWorkerCalendar] = useState(false);
  const [calendarData, setCalendarData] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date().toISOString().slice(0, 7));
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarSearch, setCalendarSearch] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [istTime, setIstTime] = useState('');
  
  // Visit Order Enforcement State
  const [visitOrderData, setVisitOrderData] = useState(null);
  
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user) {
      navigate('/supervisor/login');
      return;
    }
    fetchParks();
    fetchServerTime();
    
    // Update time every minute
    const timeInterval = setInterval(fetchServerTime, 120000); // Reduced from 60s to 120s to save reads
    return () => clearInterval(timeInterval);
  }, [user, navigate, authLoading]);

  const fetchServerTime = async () => {
    try {
      const response = await api.get('/server-time');
      setIstTime(response.data.ist_time);
    } catch (error) {
      // Fallback to local IST calculation
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istTime = new Date(now.getTime() + istOffset);
      setIstTime(istTime.toISOString().slice(0, 19).replace('T', ' '));
    }
  };

  const fetchParks = async () => {
    setLoading(true);
    try {
      const [parksResponse, otResponse, visitOrderResponse] = await Promise.all([
        supervisorAPI.getAssignedParks(user.supervisor_id),
        supervisorAPI.getOTParks(user.supervisor_id),
        supervisorAPI.getCurrentVisitOrder(user.supervisor_id).catch(() => ({ data: null }))
      ]);
      setParks(parksResponse.data.parks);
      setOtParks(otResponse.data.ot_parks || []);
      setVisitOrderData(visitOrderResponse.data);
    } catch (error) {
      toast.error('Failed to load parks');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/supervisor/login');
  };

  const handleChangePin = async () => {
    if (!currentPin || !newPin || !confirmPin) {
      toast.error('Please fill all fields');
      return;
    }
    if (newPin !== confirmPin) {
      toast.error('New PIN and Confirm PIN do not match');
      return;
    }
    if (newPin.length < 4) {
      toast.error('PIN must be at least 4 digits');
      return;
    }

    try {
      await supervisorAPI.changePin({
        supervisor_id: user.supervisor_id,
        current_pin: currentPin,
        new_pin: newPin
      });
      toast.success('PIN changed successfully!');
      setShowChangePinModal(false);
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change PIN');
    }
  };

  const fetchWorkerCalendar = async (month) => {
    if (!user?.supervisor_id) return;
    setCalendarLoading(true);
    try {
      const response = await supervisorAPI.getWorkerCalendar(user.supervisor_id, month);
      setCalendarData(response.data);
    } catch (error) {
      toast.error('Failed to load worker calendar');
    } finally {
      setCalendarLoading(false);
    }
  };

  const openWorkerCalendar = () => {
    setShowWorkerCalendar(true);
    fetchWorkerCalendar(calendarMonth);
  };

  const changeCalendarMonth = (direction) => {
    const [year, month] = calendarMonth.split('-').map(Number);
    let newMonth = month + direction;
    let newYear = year;
    if (newMonth > 12) { newMonth = 1; newYear++; }
    if (newMonth < 1) { newMonth = 12; newYear--; }
    const newMonthStr = `${newYear}-${String(newMonth).padStart(2, '0')}`;
    setCalendarMonth(newMonthStr);
    fetchWorkerCalendar(newMonthStr);
  };

  // Get days in month for calendar
  const getDaysInMonth = (monthStr) => {
    const [year, month] = monthStr.split('-').map(Number);
    return new Date(year, month, 0).getDate();
  };

  // Sort parks alphabetically by name
  const sortParksAlphabetically = (parksArr) => {
    if (!parksArr) return [];
    return [...parksArr].sort((a, b) => 
      (a.park_name || '').localeCompare(b.park_name || '')
    );
  };

  // Sort parks by visit order combination when enforcement is active
  const sortParksByVisitOrder = (parksArr) => {
    if (!parksArr || !visitOrderData?.has_combinations || !visitOrderData?.visit_order) {
      return sortParksAlphabetically(parksArr);
    }
    
    const visitOrder = visitOrderData.visit_order;
    
    return [...parksArr].sort((a, b) => {
      const aIndex = visitOrder.indexOf(a.park_id);
      const bIndex = visitOrder.indexOf(b.park_id);
      
      // Parks in the visit order come first, sorted by their position
      if (aIndex >= 0 && bIndex >= 0) {
        return aIndex - bIndex;
      }
      // Parks in visit order come before parks not in the order
      if (aIndex >= 0) return -1;
      if (bIndex >= 0) return 1;
      // Parks not in the order are sorted alphabetically
      return (a.park_name || '').localeCompare(b.park_name || '');
    });
  };

  // Check if park is allowed to be visited (visit order enforcement)
  const isParkAllowedToVisit = (parkId) => {
    // If no enforcement, all parks are allowed
    if (!visitOrderData?.visit_order_enforced) return true;
    // If all completed, allow any remaining
    if (visitOrderData?.all_completed) return true;
    // Only the next park in order is allowed
    return parkId === visitOrderData?.next_park_id;
  };

  // Get park's position in the visit order
  const getParkOrderPosition = (parkId) => {
    if (!visitOrderData?.has_combinations || !visitOrderData?.visit_order) return null;
    const index = visitOrderData.visit_order.indexOf(parkId);
    return index >= 0 ? index + 1 : null;
  };

  const filteredParks = parks.filter(park => {
    // First apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        (park.park_name || '').toLowerCase().includes(search) ||
        (park.park_id || '').toLowerCase().includes(search) ||
        (park.zone || '').toLowerCase().includes(search) ||
        (park.division || '').toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    // Then apply status filter
    if (filter === 'completed') return park.visited_today && park.is_active !== false;
    if (filter === 'pending') return !park.visited_today && !park.is_incomplete && park.is_active !== false;
    if (filter === 'incomplete') return park.is_incomplete && park.is_active !== false;
    if (filter === 'renovation') return park.is_active === false;
    if (filter === 'ot') return false; // OT parks shown separately
    return true;
  });
  
  // Filter OT parks by search
  const filteredOtParks = otParks.filter(park => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        park.park_name?.toLowerCase().includes(search) ||
        park.zone?.toLowerCase().includes(search) ||
        park.division?.toLowerCase().includes(search) ||
        park.park_id?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    return true;
  });

  // Calculate counts - exclude inactive parks from pending count
  const activeParks = parks.filter(p => p.is_active !== false);
  const inactiveParks = parks.filter(p => p.is_active === false);
  const renovationCount = inactiveParks.length;
  const incompleteCount = activeParks.filter(p => p.is_incomplete).length;
  const completedCount = activeParks.filter(p => p.visited_today && !p.is_incomplete).length;
  const pendingCount = activeParks.filter(p => !p.visited_today && !p.is_incomplete).length;
  
  // OT parks counts
  const otCompleted = otParks.filter(p => p.visited_today).length;
  const otTotal = otParks.length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Park Supervisor
            </h1>
            <p className="text-sm opacity-90">{user?.supervisor_name || user?.supervisor_id}</p>
            {istTime && (
              <p className="text-xs opacity-75 mt-1">
                <Clock className="w-3 h-3 inline mr-1" />
                {istTime} IST
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/supervisor/pending-attendance"
              className="p-2 bg-red-500 rounded-lg hover:bg-red-600"
              title="Pending Attendance"
              data-testid="pending-attendance-link"
            >
              <UserX className="w-5 h-5" />
            </Link>
            <button
              onClick={openWorkerCalendar}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30"
              title="Worker Calendar"
            >
              <Calendar className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowChangePinModal(true)}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30"
              title="Change PIN"
            >
              <Key className="w-5 h-5" />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Summary */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-lg">Today's Tasks</h2>
            <button
              onClick={fetchParks}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <RefreshCw className="w-5 h-5 text-slate-600" />
            </button>
          </div>
          <div className="grid grid-cols-5 gap-2 text-center">
            <div className="bg-slate-100 rounded-lg p-3">
              <div className="text-2xl font-bold text-slate-700">{activeParks.length}</div>
              <div className="text-xs text-slate-500 font-semibold">ACTIVE</div>
            </div>
            <div className="bg-green-100 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-600">{completedCount}</div>
              <div className="text-xs text-green-600 font-semibold">DONE</div>
            </div>
            <div className="bg-orange-100 rounded-lg p-3">
              <div className="text-2xl font-bold text-orange-600">{pendingCount + incompleteCount}</div>
              <div className="text-xs text-orange-600 font-semibold">PENDING</div>
            </div>
            <div className="bg-amber-100 rounded-lg p-3">
              <div className="text-2xl font-bold text-amber-700">{otCompleted}/{otTotal}</div>
              <div className="text-xs text-amber-700 font-semibold">OT</div>
            </div>
            <div className="bg-yellow-100 rounded-lg p-3">
              <div className="text-2xl font-bold text-yellow-700">{renovationCount}</div>
              <div className="text-xs text-yellow-700 font-semibold">RENOV.</div>
            </div>
          </div>
        </div>

        {/* Visit Order Enforcement Banner */}
        {visitOrderData?.visit_order_enforced && visitOrderData?.has_combinations && (
          <div className="bg-purple-100 border-2 border-purple-300 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                {visitOrderData.active_combination_index + 1}
              </div>
              <div>
                <h3 className="font-bold text-purple-800">Strict Visit Order Active</h3>
                <p className="text-sm text-purple-600">
                  Visit parks in sequence. Next: #{(visitOrderData.next_park_index || 0) + 1}
                </p>
              </div>
            </div>
            {visitOrderData.all_completed && (
              <div className="bg-green-100 text-green-700 px-3 py-2 rounded-lg text-sm font-semibold">
                <CheckCircle2 className="w-4 h-4 inline mr-1" />
                All ordered parks completed!
              </div>
            )}
          </div>
        )}

        {/* Search Box */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search parks by name, zone, or division..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-12 pl-12 pr-4 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
            data-testid="park-search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 min-w-[70px] h-12 rounded-lg font-semibold uppercase text-sm transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white border-2 border-slate-200 text-slate-700'
            }`}
          >
            All ({parks.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`flex-1 min-w-[70px] h-12 rounded-lg font-semibold uppercase text-sm transition-colors ${
              filter === 'pending'
                ? 'bg-blue-600 text-white'
                : 'bg-white border-2 border-slate-200 text-slate-700'
            }`}
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`flex-1 min-w-[70px] h-12 rounded-lg font-semibold uppercase text-sm transition-colors ${
              filter === 'completed'
                ? 'bg-blue-600 text-white'
                : 'bg-white border-2 border-slate-200 text-slate-700'
            }`}
          >
            Done ({completedCount})
          </button>
          {otTotal > 0 && (
            <button
              onClick={() => setFilter('ot')}
              className={`flex-1 min-w-[70px] h-12 rounded-lg font-semibold uppercase text-sm transition-colors ${
                filter === 'ot'
                  ? 'bg-amber-600 text-white'
                  : 'bg-amber-100 border-2 border-amber-300 text-amber-700'
              }`}
            >
              OT ({otCompleted}/{otTotal})
            </button>
          )}
          {incompleteCount > 0 && (
            <button
              onClick={() => setFilter('incomplete')}
              className={`flex-1 min-w-[70px] h-12 rounded-lg font-semibold uppercase text-sm transition-colors ${
                filter === 'incomplete'
                  ? 'bg-orange-600 text-white'
                  : 'bg-orange-100 border-2 border-orange-300 text-orange-700'
              }`}
            >
              Incomplete ({incompleteCount})
            </button>
          )}
          {renovationCount > 0 && (
            <button
              onClick={() => setFilter('renovation')}
              className={`flex-1 min-w-[70px] h-12 rounded-lg font-semibold uppercase text-sm transition-colors ${
                filter === 'renovation'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-yellow-100 border-2 border-yellow-300 text-yellow-700'
              }`}
            >
              Renovation ({renovationCount})
            </button>
          )}
        </div>

        {/* Parks List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filter === 'ot' ? (
          // Show OT Parks
          filteredOtParks.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-slate-600">No OT parks assigned for today</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOtParks.map((park) => (
                <div
                  key={`ot-${park.park_id}`}
                  className={`bg-white border-2 rounded-xl p-4 transition-all ${
                    park.visited_today
                      ? 'border-green-200 bg-green-50'
                      : 'border-amber-300 hover:border-amber-500'
                  }`}
                  data-testid={`ot-park-card-${park.park_id}`}
                >
                  {/* OT Badge */}
                  <div className="bg-amber-100 text-amber-700 px-3 py-1 rounded-lg mb-3 inline-flex items-center gap-2 text-sm font-bold">
                    <Clock className="w-4 h-4" />
                    OT - Covering for {park.absent_supervisor_name}
                  </div>
                  
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900">{park.park_name}</h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
                        <MapPin className="w-4 h-4" />
                        <span className="font-semibold">Zone {park.zone}</span>
                        {park.division && <span className="text-slate-400">• Div: {park.division}</span>}
                      </div>
                      <div className="text-sm text-slate-500 mt-1">Manpower: {park.manpower_count || 0}</div>
                    </div>
                    {park.visited_today ? (
                      <div className="flex items-center gap-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        <CheckCircle2 className="w-4 h-4" />
                        DONE
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        <Clock className="w-4 h-4" />
                        PENDING
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <a
                      href={`https://www.google.com/maps?q=${park.gps_coordinates?.lat || park.gps_lat || 0},${park.gps_coordinates?.lng || park.gps_long || 0}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 h-10 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg font-semibold flex items-center justify-center gap-2 text-sm"
                    >
                      <MapPin className="w-4 h-4" />
                      Map
                    </a>
                    {!park.visited_today && (
                      <button
                        onClick={() => navigate(`/supervisor/inspection/${park.park_id}?ot=true`)}
                        className="flex-1 h-10 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-sm"
                      >
                        Start OT Visit
                      </button>
                    )}
                    {park.visited_today && park.can_edit && (
                      <button
                        onClick={() => navigate(`/supervisor/inspection/${park.park_id}/edit/${park.inspection_id}?ot=true`)}
                        className="flex-1 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold text-sm"
                      >
                        Edit ({park.edit_minutes_remaining}m)
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : filteredParks.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg">
            <p className="text-slate-600">No parks found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Sort by visit order when enforcement is active, otherwise alphabetically */}
            {(visitOrderData?.visit_order_enforced ? sortParksByVisitOrder(filteredParks) : sortParksAlphabetically(filteredParks)).map((park) => (
              <div
                key={park.park_id}
                className={`bg-white border-2 rounded-xl p-4 transition-all ${
                  park.visited_today
                    ? 'border-green-200 bg-green-50 opacity-75'
                    : park.is_incomplete
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-slate-200 hover:border-blue-600'
                }`}
                data-testid={`park-card-${park.park_id}`}
              >
                {/* Incomplete Warning Banner */}
                {park.is_incomplete && (
                  <div className="bg-orange-500 text-white px-3 py-2 rounded-lg mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold text-sm">Incomplete - Click to restart inspection</span>
                  </div>
                )}
                
                {/* Under Renovation Banner */}
                {park.is_active === false && (
                  <div className="bg-yellow-500 text-white px-3 py-2 rounded-lg mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold text-sm">Under Renovation - Not required to visit</span>
                  </div>
                )}
                
                {/* Assigned to OT Banner */}
                {park.assigned_to_ot && (
                  <div className="bg-amber-500 text-white px-3 py-2 rounded-lg mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span className="font-semibold text-sm">Assigned to OT - {park.ot_supervisor_name} is covering</span>
                  </div>
                )}
                
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {/* Visit Order Position Badge */}
                      {visitOrderData?.has_combinations && getParkOrderPosition(park.park_id) && (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          visitOrderData?.visit_order_enforced && isParkAllowedToVisit(park.park_id)
                            ? 'bg-purple-500 text-white'
                            : 'bg-slate-200 text-slate-600'
                        }`}>
                          {getParkOrderPosition(park.park_id)}
                        </div>
                      )}
                      <h3 className="text-lg font-bold text-slate-900">{park.park_name}</h3>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
                      <MapPin className="w-4 h-4" />
                      <span className="font-semibold">{park.zone}</span>
                      {park.division && (
                        <span className="text-slate-400">• Div: {park.division}</span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      Manpower: {park.manpower_list?.length || 0}
                    </div>
                  </div>
                  {park.assigned_to_ot ? (
                    <div className="flex items-center gap-2 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      <Clock className="w-4 h-4" />
                      OT
                    </div>
                  ) : park.is_active === false ? (
                    <div className="flex items-center gap-2 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      <AlertTriangle className="w-4 h-4" />
                      RENOVATION
                    </div>
                  ) : park.visited_today ? (
                    <div className="flex items-center gap-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      <CheckCircle2 className="w-4 h-4" />
                      DONE
                    </div>
                  ) : park.is_incomplete ? (
                    <div className="flex items-center gap-2 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      <AlertTriangle className="w-4 h-4" />
                      INCOMPLETE
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 bg-slate-400 text-white px-3 py-1 rounded-full text-sm font-bold">
                      <Clock className="w-4 h-4" />
                      PENDING
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <a
                    href={`https://www.google.com/maps?q=${park.gps_coordinates?.lat || park.gps_lat || 0},${park.gps_coordinates?.lng || park.gps_long || 0}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 h-10 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg font-semibold flex items-center justify-center gap-2 text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MapPin className="w-4 h-4" />
                    View on Map
                  </a>
                  {!park.visited_today && !park.is_incomplete && park.is_active !== false && !park.assigned_to_ot && (
                    visitOrderData?.visit_order_enforced && !isParkAllowedToVisit(park.park_id) ? (
                      <div className="flex-1 h-10 bg-slate-300 text-slate-500 rounded-lg font-semibold text-sm flex items-center justify-center cursor-not-allowed">
                        Wait for #{getParkOrderPosition(park.park_id)}
                      </div>
                    ) : (
                      <button
                        onClick={() => navigate(`/supervisor/inspection/${park.park_id}`)}
                        className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm"
                      >
                        Start Visit
                      </button>
                    )
                  )}
                  {park.assigned_to_ot && (
                    <div className="flex-1 h-10 bg-slate-300 text-slate-500 rounded-lg font-semibold text-sm flex items-center justify-center cursor-not-allowed">
                      Assigned to OT
                    </div>
                  )}
                  {park.is_incomplete && (
                    <button
                      onClick={() => navigate(`/supervisor/inspection/${park.park_id}`)}
                      className="flex-1 h-10 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
                      data-testid={`continue-inspection-${park.park_id}`}
                    >
                      <RefreshCw className="w-4 h-4" />
                      Restart Inspection
                    </button>
                  )}
                  {park.visited_today && park.can_edit && (
                    <button
                      onClick={() => navigate(`/supervisor/inspection/${park.park_id}/edit/${park.inspection_id}`)}
                      className="flex-1 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
                      data-testid={`edit-inspection-${park.park_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit ({park.edit_minutes_remaining}m left)
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Change PIN Modal */}
      {showChangePinModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-lg">Change PIN</h3>
              <button onClick={() => setShowChangePinModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-600 block mb-1">Current PIN</label>
                <input
                  type="password"
                  value={currentPin}
                  onChange={(e) => setCurrentPin(e.target.value)}
                  className="w-full h-12 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600"
                  maxLength="6"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600 block mb-1">New PIN</label>
                <input
                  type="password"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  className="w-full h-12 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600"
                  maxLength="6"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600 block mb-1">Confirm New PIN</label>
                <input
                  type="password"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value)}
                  className="w-full h-12 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600"
                  maxLength="6"
                />
              </div>
              <button
                onClick={handleChangePin}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold"
              >
                Update PIN
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Worker Calendar Modal */}
      {showWorkerCalendar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="bg-green-600 text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Worker Attendance Calendar</h3>
                <p className="text-sm opacity-90">Your assigned parks</p>
              </div>
              <button 
                onClick={() => setShowWorkerCalendar(false)}
                className="p-2 hover:bg-white/20 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Month Navigator */}
            <div className="p-4 border-b flex items-center justify-center gap-4">
              <button 
                onClick={() => changeCalendarMonth(-1)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-lg font-bold min-w-[150px] text-center">
                {new Date(calendarMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <button 
                onClick={() => changeCalendarMonth(1)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Search Box */}
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by park name, worker name, or zone..."
                  value={calendarSearch}
                  onChange={(e) => setCalendarSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:border-green-500 focus:ring-0"
                />
              </div>
            </div>

            <div className="overflow-auto max-h-[55vh] p-4">
              {calendarLoading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : !calendarData?.workers || calendarData.workers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No workers found in your assigned parks</p>
              ) : (
                <div className="space-y-6">
                  {/* Group by park - sorted alphabetically and filtered by search */}
                  {sortParksAlphabetically(calendarData.parks || [])
                    .filter(park => {
                      if (!calendarSearch) return true;
                      const search = calendarSearch.toLowerCase();
                      const parkMatch = park.park_name?.toLowerCase().includes(search) ||
                                       park.zone?.toString().includes(search) ||
                                       park.division?.toString().includes(search);
                      const workerMatch = calendarData.workers
                        .filter(w => w.park_id === park.park_id)
                        .some(w => w.employee_name?.toLowerCase().includes(search));
                      return parkMatch || workerMatch;
                    })
                    .map(park => (
                    <div key={park.park_id} className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-100 p-3 font-bold flex justify-between items-center">
                        <span>{park.park_name}</span>
                        <span className="text-sm font-normal bg-green-100 text-green-700 px-2 py-1 rounded">
                          Zone {park.zone} | Div {park.division || '-'}
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-50">
                              <th className="text-left p-2 font-bold sticky left-0 bg-slate-50 min-w-[120px]">Worker</th>
                              <th className="text-left p-2 font-bold min-w-[80px]">Category</th>
                              {Array.from({ length: getDaysInMonth(calendarMonth) }, (_, i) => (
                                <th key={i} className="text-center p-1 font-bold w-8">
                                  {i + 1}
                                </th>
                              ))}
                              <th className="text-center p-2 font-bold bg-green-100 text-green-700 min-w-[40px]">P</th>
                              <th className="text-center p-2 font-bold bg-red-100 text-red-700 min-w-[40px]">A</th>
                            </tr>
                          </thead>
                          <tbody>
                            {calendarData.workers
                              .filter(w => w.park_id === park.park_id)
                              .map((worker, idx) => {
                                // Calculate total present and absent
                                const attendance = worker.attendance || {};
                                const presentCount = Object.values(attendance).filter(s => s === 'Present').length;
                                const absentCount = Object.values(attendance).filter(s => s === 'Absent').length;
                                
                                return (
                                  <tr key={idx} className={`border-t ${worker.is_temporary ? 'bg-red-50' : worker.is_deleted ? 'bg-slate-100' : ''}`}>
                                    <td className={`p-2 sticky left-0 font-medium ${
                                      worker.is_temporary ? 'bg-red-50 text-red-700' : 
                                      worker.is_deleted ? 'bg-slate-100 text-slate-500 italic' : 'bg-white'
                                    }`}>
                                      {worker.employee_name}
                                      {worker.is_temporary && <span className="ml-1 text-xs">(Temporary)</span>}
                                      {worker.is_deleted && <span className="ml-1 text-xs text-red-500">(Removed)</span>}
                                    </td>
                                    <td className={`p-2 ${
                                      worker.is_temporary ? 'text-red-600 font-semibold' : 
                                      worker.is_deleted ? 'text-slate-400 italic' : 'text-slate-500'
                                    }`}>
                                      {worker.category || '-'}
                                    </td>
                                    {Array.from({ length: getDaysInMonth(calendarMonth) }, (_, i) => {
                                      const day = String(i + 1).padStart(2, '0');
                                      const dateStr = `${calendarMonth}-${day}`;
                                      const status = worker.attendance?.[dateStr];
                                      return (
                                        <td key={i} className="text-center p-1">
                                          {status ? (
                                            <span className={`inline-block w-6 h-6 rounded-full text-white text-[10px] leading-6 ${
                                              status === 'Present' ? 'bg-green-500' : 'bg-red-400'
                                            }`}>
                                              {status === 'Present' ? 'P' : 'A'}
                                            </span>
                                          ) : (
                                            <span className="text-slate-300">-</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                    <td className="text-center p-2 bg-green-50 font-bold text-green-700">{presentCount}</td>
                                    <td className="text-center p-2 bg-red-50 font-bold text-red-700">{absentCount}</td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupervisorDashboard;
